// map-to-consumption-data.ts
import { HeatConsumptionResponse, TelemetryEntry } from '../models/household-data';
import { ConsumptionData } from '../components/consumption-table/consumption-table';

export function mapToConsumptionDataDownsampled(
  apiResponse: HeatConsumptionResponse,
  startDate: Date,
  endDate: Date,
  outsideTempTelemetry?: TelemetryEntry | null
): ConsumptionData[] {
  if (!apiResponse?.heatConsumpion?.telemetry?.length) return [];

  const consumptionTelemetry = apiResponse.heatConsumpion.telemetry.find(t => t.measurementTypeId === 1);
  const consumptionLineColor = consumptionTelemetry?.lineColour;
  // @ts-ignore
  const meterTelemetry = apiResponse.heatConsumpion.telemetry.find(t => t.measurementTypeId === 2);
  const meterTelemetryLineColor = meterTelemetry?.lineColour;
  // @ts-ignore
  const flowTempTelemetry = apiResponse.heatConsumpion.telemetry.find(t => t.measurementTypeId === 5);
  const flowLineColor = flowTempTelemetry?.lineColour;
  const outdoorLineColor = outsideTempTelemetry?.lineColour;

  if (!consumptionTelemetry || !meterTelemetry) return [];

  const rangeMs = endDate.getTime() - startDate.getTime();
  const dayMs = 24 * 60 * 60 * 1000;
  let bucketMs: number | null = null;

  const rangeDays = rangeMs / dayMs;
  const MONTHLY_THRESHOLD_DAYS = 31;

  if (rangeDays <= 1 && rangeDays < 2) {
    bucketMs = 60 * 60 * 1000;          // 1h
  } else if (rangeDays >= 2 && rangeDays <= 5) {
    bucketMs = 2 * 60 * 60 * 1000;      // 2h
  } else if (rangeDays > 5 && rangeDays <= MONTHLY_THRESHOLD_DAYS) {
    bucketMs = dayMs;                   // 1d
  } else if (rangeDays > MONTHLY_THRESHOLD_DAYS) {
    bucketMs = -1;                      // Monats-Modus (Sonderbehandlung)
  } else {
    // kein Downsampling -> Rohdaten zusammenzippen (inkl. Temperaturen)
    return zipRaw(
      consumptionTelemetry.timestamps as Date[],
      consumptionTelemetry.values as (number | null)[],
      meterTelemetry.values as (number | null)[],
      flowTempTelemetry?.values as (number | null)[] | undefined,
      outsideTempTelemetry?.values as (number | null)[] | undefined,
      consumptionLineColor,
      meterTelemetryLineColor,
      flowLineColor,
      outdoorLineColor
    );
  }

  const ts = consumptionTelemetry.timestamps as Date[];
  const consVals = consumptionTelemetry.values as (number | null)[];
  const meterVals = meterTelemetry.values as (number | null)[];

  // Vorlauftemperatur kann fehlen -> defensiv behandeln
  const tempVals = (flowTempTelemetry?.values as (number | null)[]) ?? [];
  const tempTs   = (flowTempTelemetry?.timestamps as Date[]) ?? [];

  // Außentemperatur kann fehlen -> defensiv behandeln
  const outdoorVals = (outsideTempTelemetry?.values as (number | null)[]) ?? [];
  const outdoorTs   = (outsideTempTelemetry?.timestamps as Date[]) ?? [];

  type Acc = {
    consSum: number;
    meterLast: number | null;
    tempSum: number;
    tempCount: number;
    outdoorTempSum: number;
    outdoorTempCount: number;
    bucketStart: number;
  };
  const buckets = new Map<number, Acc>();

  const startMs = startDate.getTime();
  const endMs   = endDate.getTime();

  const emptyAcc = (bucketStart: number): Acc => ({
    consSum: 0, meterLast: null,
    tempSum: 0, tempCount: 0,
    outdoorTempSum: 0, outdoorTempCount: 0,
    bucketStart
  });

  const getBucketStart = (tMs: number): number => {
    if (bucketMs === -1)    return localMonthStartMs(tMs);
    if (bucketMs === dayMs) return localDayStartMs(tMs);
    return Math.floor(tMs / bucketMs!) * bucketMs!;
  };

  // 1) Verbrauch + Zählerstände einsortieren
  for (let i = 0; i < ts.length; i++) {
    const tMs = ts[i].getTime();
    if (tMs < startMs || tMs >= endMs) continue;

    const bucketStartMs = getBucketStart(tMs);

    let acc = buckets.get(bucketStartMs);
    if (!acc) {
      acc = emptyAcc(bucketStartMs);
      buckets.set(bucketStartMs, acc);
    }

    const c = consVals[i];
    if (c != null) acc.consSum += c;

    const m = meterVals[i];
    if (m != null) acc.meterLast = m;
  }

  // 2) Vorlauftemperatur in Buckets mitteln
  for (let i = 0; i < tempTs.length; i++) {
    const t = tempTs[i];
    if (!(t instanceof Date)) continue;
    const tMs = t.getTime();
    if (tMs < startMs || tMs >= endMs) continue;

    const bucketStartMs = getBucketStart(tMs);
    let acc = buckets.get(bucketStartMs);
    if (!acc) {
      acc = emptyAcc(bucketStartMs);
      buckets.set(bucketStartMs, acc);
    }

    const temp = tempVals[i];
    if (typeof temp === 'number') {
      acc.tempSum += temp;
      acc.tempCount += 1;
    }
  }

  // 3) Außentemperatur in Buckets mitteln
  for (let i = 0; i < outdoorTs.length; i++) {
    const t = outdoorTs[i];
    if (!(t instanceof Date)) continue;
    const tMs = t.getTime();
    if (tMs < startMs || tMs >= endMs) continue;

    const bucketStartMs = getBucketStart(tMs);
    let acc = buckets.get(bucketStartMs);
    if (!acc) {
      acc = emptyAcc(bucketStartMs);
      buckets.set(bucketStartMs, acc);
    }

    const temp = outdoorVals[i];
    if (typeof temp === 'number') {
      acc.outdoorTempSum += temp;
      acc.outdoorTempCount += 1;
    }
  }

  // sortiert nach Zeitstempel (numerisch)
  const sortedKeys = Array.from(buckets.keys()).sort((a, b) => a - b);

  const rows: ConsumptionData[] = [];
  for (const k of sortedKeys) {
    const acc = buckets.get(k)!;
    if (acc.consSum === 0 && acc.meterLast == null && acc.tempCount === 0 && acc.outdoorTempCount === 0) continue;

    const avgFlowTemp    = acc.tempCount > 0        ? round1(acc.tempSum / acc.tempCount)               : null;
    const avgOutdoorTemp = acc.outdoorTempCount > 0 ? round1(acc.outdoorTempSum / acc.outdoorTempCount) : null;

    rows.push({
      date: new Date(acc.bucketStart),
      consumption: round1(acc.consSum),
      meter_amount: acc.meterLast as any,
      flow_temperature: avgFlowTemp,
      outside_temperature: avgOutdoorTemp,
      consumption_line_color: consumptionLineColor,
      meter_line_color: meterTelemetryLineColor,
      flow_line_color: flowLineColor,
      outside_line_color: outdoorLineColor,
    } as ConsumptionData);
  }

  const colors = {
    consumption_line_color: consumptionLineColor,
    meter_line_color: meterTelemetryLineColor,
    flow_line_color: flowLineColor,
    outside_line_color: outdoorLineColor,
  };

  const fillEndMs = Math.min(endMs, Date.now());

  // Für stündliche Ansicht: Lücken mit letztem bekannten Zählerstand füllen
  if (bucketMs === 60 * 60 * 1000) {
    return fillHourlyGaps(rows, startMs, fillEndMs, colors);
  }

  // Für tägliche Ansicht (Wochen-/Monatsansicht): jeden Kalendertag sicherstellen
  if (bucketMs === dayMs) {
    return fillDailyGaps(rows, startMs, fillEndMs, colors);
  }

  // Für Jahresansicht: jeden Monatsersten sicherstellen
  if (bucketMs === -1) {
    return fillMonthlyGaps(rows, startMs, fillEndMs, colors);
  }

  return rows;
}

function zipRaw(
  timestamps: Date[],
  cons: (number | null)[],
  meter: (number | null)[],
  flowTemp?: (number | null)[],
  outdoorTemp?: (number | null)[],
  consumptionLineColor?: string,
  meterLineColor?: string,
  flowLineColor?: string,
  outdoorLineColor?: string
): ConsumptionData[] {
  const len = Math.min(
    timestamps.length,
    cons.length,
    meter.length,
    flowTemp   ? flowTemp.length   : Number.MAX_SAFE_INTEGER,
    outdoorTemp ? outdoorTemp.length : Number.MAX_SAFE_INTEGER
  );

  const out: ConsumptionData[] = [];
  for (let i = 0; i < len; i++) {
    out.push({
      date: timestamps[i],
      consumption: cons[i] as any,
      meter_amount: meter[i] as any,
      flow_temperature: flowTemp ? (flowTemp[i] ?? null) : null,
      outside_temperature: outdoorTemp ? (outdoorTemp[i] ?? null) : null,
      consumption_line_color: consumptionLineColor,
      meter_line_color: meterLineColor,
      flow_line_color: flowLineColor,
      outside_line_color: outdoorLineColor,
    } as ConsumptionData);
  }
  return out;
}

// Hilfsfunktion: Rundet zuverlässig auf 1 Nachkommastelle
function round1(n: number): number {
  return Math.round((n + Number.EPSILON) * 10) / 10;
}

type Colors = Pick<ConsumptionData, 'consumption_line_color' | 'meter_line_color' | 'flow_line_color' | 'outside_line_color'>;

function gapRow(tMs: number, lastMeter: number, colors: Colors): ConsumptionData {
  return {
    date: new Date(tMs),
    consumption: null,
    meter_amount: lastMeter,
    flow_temperature: null,
    outside_temperature: null,
    ...colors,
  } as ConsumptionData;
}

// Füllt fehlende Stunden-Slots mit dem letzten bekannten Zählerstand (carry-forward)
function fillHourlyGaps(
  rows: ConsumptionData[],
  startMs: number,
  endMs: number,
  colors: Colors
): ConsumptionData[] {
  if (rows.length === 0) return rows;

  const hourMs = 60 * 60 * 1000;
  const byTime = new Map<number, ConsumptionData>();
  for (const r of rows) {
    const t = r.date instanceof Date ? r.date.getTime() : new Date(r.date).getTime();
    byTime.set(t, r);
  }

  const firstBucket = Math.floor(startMs / hourMs) * hourMs;
  const result: ConsumptionData[] = [];
  let lastMeter: number | null = null;

  for (let t = firstBucket; t < endMs; t += hourMs) {
    const existing = byTime.get(t);
    if (existing) {
      if (typeof existing.meter_amount === 'number') lastMeter = existing.meter_amount;
      result.push(existing);
    } else if (lastMeter !== null) {
      result.push(gapRow(t, lastMeter, colors));
    }
  }

  return result;
}

// Füllt fehlende Kalendertage mit dem letzten bekannten Zählerstand (carry-forward).
// Verwendet lokale Mitternacht als Bucket-Grenze, damit alle Einträge 00:00:00 lokaler Zeit haben.
function fillDailyGaps(
  rows: ConsumptionData[],
  startMs: number,
  endMs: number,
  colors: Colors
): ConsumptionData[] {
  if (rows.length === 0) return rows;

  const byDayStart = new Map<number, ConsumptionData>();
  for (const r of rows) {
    const t = r.date instanceof Date ? r.date.getTime() : new Date(r.date).getTime();
    byDayStart.set(localDayStartMs(t), r);
  }

  const result: ConsumptionData[] = [];
  let lastMeter: number | null = null;

  let current = localDayStartMs(startMs);
  while (current < endMs) {
    const existing = byDayStart.get(current);
    if (existing) {
      if (typeof existing.meter_amount === 'number') lastMeter = existing.meter_amount;
      result.push(existing);
    } else if (lastMeter !== null) {
      result.push(gapRow(current, lastMeter, colors));
    }
    // Nächsten Tag via Calendar-Arithmetik (DST-sicher)
    const next = new Date(current);
    next.setDate(next.getDate() + 1);
    current = next.getTime();
  }

  return result;
}

function localDayStartMs(tMs: number): number {
  const d = new Date(tMs);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

function localMonthStartMs(tMs: number): number {
  const d = new Date(tMs);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// Füllt fehlende Monate mit dem letzten bekannten Zählerstand (carry-forward).
// Jeder Eintrag liegt auf dem 1. des Monats um 00:00:00 lokaler Zeit.
function fillMonthlyGaps(
  rows: ConsumptionData[],
  startMs: number,
  endMs: number,
  colors: Colors
): ConsumptionData[] {
  if (rows.length === 0) return rows;

  const byMonthStart = new Map<number, ConsumptionData>();
  for (const r of rows) {
    const t = r.date instanceof Date ? r.date.getTime() : new Date(r.date).getTime();
    byMonthStart.set(localMonthStartMs(t), r);
  }

  const result: ConsumptionData[] = [];
  let lastMeter: number | null = null;

  let current = localMonthStartMs(startMs);
  while (current < endMs) {
    const existing = byMonthStart.get(current);
    if (existing) {
      if (typeof existing.meter_amount === 'number') lastMeter = existing.meter_amount;
      result.push(existing);
    } else if (lastMeter !== null) {
      result.push(gapRow(current, lastMeter, colors));
    }
    // Nächsten Monat via Calendar-Arithmetik
    const next = new Date(current);
    next.setMonth(next.getMonth() + 1);
    current = next.getTime();
  }

  return result;
}
